import React, { useState, useEffect } from 'react';
import { 
  getUserActiveSubscription, 
  getUserSubscriptions, 
  cancelSubscription,
  SUBSCRIPTION_STATUS 
} from '../services/subscriptionService';
import LoadingSpinner from './LoadingSpinner';

const SubscriptionDashboard = ({ user, userData }) => {
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (user?.uid) {
      fetchSubscriptionData();
    }
  }, [user]);

  const fetchSubscriptionData = async () => {
    setLoading(true);
    
    try {
      const [currentResult, historyResult] = await Promise.all([
        getUserActiveSubscription(user.uid),
        getUserSubscriptions(user.uid)
      ]);

      if (currentResult.success) {
        setCurrentSubscription(currentResult.subscription);
      }

      if (historyResult.success) {
        setSubscriptionHistory(historyResult.subscriptions);
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!currentSubscription || !window.confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    setCancelling(true);
    try {
      const result = await cancelSubscription(currentSubscription.id);
      if (result.success) {
        alert('Subscription cancelled successfully');
        fetchSubscriptionData();
      } else {
        alert('Error cancelling subscription: ' + result.error);
      }
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setCancelling(false);
    }
  };

  const getUsagePercentage = (used, limit) => {
    if (!limit) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return timestamp.toDate().toLocaleDateString();
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    const now = new Date();
    const end = endDate.toDate();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">My Subscription</h1>
        <p className="text-gray-600">Manage your subscription and view usage statistics</p>
      </div>

      {/* Current Subscription */}
      {currentSubscription ? (
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 mb-8">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  {currentSubscription.packageName}
                </h2>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>Started: {formatDate(currentSubscription.startDate)}</span>
                  <span>Expires: {formatDate(currentSubscription.endDate)}</span>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    getDaysRemaining(currentSubscription.endDate) <= 7
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {getDaysRemaining(currentSubscription.endDate)} days left
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">
                  ${currentSubscription.price}
                </div>
                <div className="text-sm text-gray-500">
                  {currentSubscription.duration} days
                </div>
              </div>
            </div>

            {/* Usage Statistics */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Usage Statistics</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {userData?.userType === 'jobseeker' ? (
                  <>
                    {currentSubscription.maxApplications && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">Applications</span>
                          <span className="text-sm text-gray-600">
                            {currentSubscription.usageStats?.applicationsUsed || 0} / {currentSubscription.maxApplications}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ 
                              width: `${getUsagePercentage(
                                currentSubscription.usageStats?.applicationsUsed || 0, 
                                currentSubscription.maxApplications
                              )}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                    )}
                    
                    {currentSubscription.resumeTemplates && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">Resume Templates</span>
                          <span className="text-sm text-gray-600">
                            Available: {currentSubscription.resumeTemplates}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {currentSubscription.profileBoost ? '✓ Profile Boost Enabled' : ''}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {currentSubscription.maxJobPostings && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">Job Postings</span>
                          <span className="text-sm text-gray-600">
                            {currentSubscription.usageStats?.jobPostingsUsed || 0} / {currentSubscription.maxJobPostings}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-green-600 h-2 rounded-full transition-all duration-300"
                            style={{ 
                              width: `${getUsagePercentage(
                                currentSubscription.usageStats?.jobPostingsUsed || 0, 
                                currentSubscription.maxJobPostings
                              )}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                    )}
                    
                    {currentSubscription.featuredJobs && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-medium text-gray-700">Featured Jobs</span>
                          <span className="text-sm text-gray-600">
                            {currentSubscription.usageStats?.featuredJobsUsed || 0} / {currentSubscription.featuredJobs}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                            style={{ 
                              width: `${getUsagePercentage(
                                currentSubscription.usageStats?.featuredJobsUsed || 0, 
                                currentSubscription.featuredJobs
                              )}%` 
                            }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Features */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Package Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {currentSubscription.features && currentSubscription.features.map((feature, index) => (
                  <div key={index} className="flex items-center text-sm text-gray-600">
                    <span className="text-green-500 mr-2">✓</span>
                    {feature}
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={() => window.location.href = '/packages'}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Upgrade Package
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancelling}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 mb-8 p-6">
          <div className="text-center">
            <div className="text-gray-400 text-6xl mb-4">📦</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">No Active Subscription</h2>
            <p className="text-gray-600 mb-4">
              You're currently using the free tier. Subscribe to a package to unlock premium features.
            </p>
            <button
              onClick={() => window.location.href = '/packages'}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Browse Packages
            </button>
          </div>
        </div>
      )}

      {/* Subscription History */}
      {subscriptionHistory.length > 0 && (
        <div className="bg-white rounded-lg shadow-lg border border-gray-200">
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Subscription History</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2">Package</th>
                    <th className="text-left py-2">Price</th>
                    <th className="text-left py-2">Duration</th>
                    <th className="text-left py-2">Start Date</th>
                    <th className="text-left py-2">End Date</th>
                    <th className="text-left py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptionHistory.map((subscription) => (
                    <tr key={subscription.id} className="border-b border-gray-100">
                      <td className="py-3">{subscription.packageName}</td>
                      <td className="py-3">${subscription.price}</td>
                      <td className="py-3">{subscription.duration} days</td>
                      <td className="py-3">{formatDate(subscription.startDate)}</td>
                      <td className="py-3">{formatDate(subscription.endDate)}</td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          subscription.status === SUBSCRIPTION_STATUS.ACTIVE
                            ? 'bg-green-100 text-green-800'
                            : subscription.status === SUBSCRIPTION_STATUS.EXPIRED
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {subscription.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionDashboard;
